import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import api, { toErrMsg } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";
import PasswordStrengthMeter, { scorePassword } from "@/components/PasswordStrengthMeter";

export default function ChangePasswordDialog({ open, onOpenChange, forced = false }) {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [busy, setBusy] = useState(false);
  const { refreshUser } = useAuth();

  useEffect(() => {
    if (!open) setForm({ current_password: "", new_password: "", confirm_password: "" });
  }, [open]);

  const submit = async () => {
    if (!form.current_password || !form.new_password) {
      toast.error("Please fill in all fields"); return;
    }
    if (form.new_password.length < 8) {
      toast.error("New password must be at least 8 characters"); return;
    }
    if (scorePassword(form.new_password) < 2) {
      toast.error("Password too weak. Mix upper + lower + digit + symbol."); return;
    }
    if (form.new_password !== form.confirm_password) {
      toast.error("New password and confirmation do not match"); return;
    }
    if (form.new_password === form.current_password) {
      toast.error("New password must be different from the current one"); return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Password changed successfully");
      if (refreshUser) await refreshUser();
      onOpenChange(false);
    } catch (e) {
      toast.error(toErrMsg(e, "Failed to change password"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open}
            onOpenChange={(v) => { if (forced && !v) return; onOpenChange(v); }}>
      <DialogContent data-testid="change-password-dialog"
                     onEscapeKeyDown={(e) => forced && e.preventDefault()}
                     onPointerDownOutside={(e) => forced && e.preventDefault()}
                     onInteractOutside={(e) => forced && e.preventDefault()}
                     hideCloseButton={forced}>
        <DialogHeader>
          <DialogTitle>
            {forced ? "Set a new password to continue" : "Change password"}
          </DialogTitle>
          <DialogDescription>
            {forced
              ? "For security, please set a new password before continuing. Your temporary password should not be reused."
              : "Choose a strong password of at least 8 characters. You will remain signed in."}
          </DialogDescription>
        </DialogHeader>
        {forced && (
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-[12px] text-amber-900">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" />
            <div>Your account is on a temporary or admin-reset password. Choose a new private password to unlock the workspace.</div>
          </div>
        )}
        <div className="grid gap-3 py-2">
          <div className="grid gap-1">
            <Label>{forced ? "Temporary password" : "Current password"}</Label>
            <Input type="password" value={form.current_password}
                   onChange={(e) => setForm({ ...form, current_password: e.target.value })}
                   data-testid="pwd-current" autoComplete="current-password" />
          </div>
          <div className="grid gap-1">
            <Label>New password</Label>
            <Input type="password" value={form.new_password}
                   onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                   data-testid="pwd-new" autoComplete="new-password" />
            <PasswordStrengthMeter password={form.new_password} testid="pwd-strength-change" />
          </div>
          <div className="grid gap-1">
            <Label>Confirm new password</Label>
            <Input type="password" value={form.confirm_password}
                   onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
                   data-testid="pwd-confirm" autoComplete="new-password" />
          </div>
        </div>
        <DialogFooter>
          {!forced && (
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="pwd-cancel">
              Cancel
            </Button>
          )}
          <Button onClick={submit} disabled={busy} data-testid="pwd-submit">
            {busy ? "Saving…" : (forced ? "Set new password" : "Change password")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
